import { ChevronRight, Circle, CircleCheck, FileText, X } from 'lucide-react'
import Button from '@components/web/Button'
import type { SetupGuideItem, SetupGuideItemKey, SetupGuideStatus } from '@lib/supabase/setupGuide.service'

type RailProps = {
  title: string
  status: SetupGuideStatus
  onOpen: () => void
  onAction: (key: SetupGuideItemKey) => void
}

type PanelProps = {
  open: boolean
  title: string
  status: SetupGuideStatus | null
  onClose: () => void
  onAction: (key: SetupGuideItemKey) => void
}

type FloatingButtonProps = {
  remainingCount: number
  onOpen: () => void
}

function ItemStatusIcon({ item }: { item: SetupGuideItem }) {
  return item.isComplete ? <CircleCheck size={18} className="text-emerald-600" /> : <Circle size={18} className="text-slate-400" />
}

export function SetupGuideRail({ title, status, onOpen, onAction }: RailProps) {
  const previewItems = status.items.filter((item) => !item.isComplete).slice(0, 4)

  if (status.remainingCount === 0) {
    return null
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-foreground">{title}</h2>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <button
          type="button"
          onClick={onOpen}
          className="min-w-[260px] border border-blue-200 bg-blue-50 p-5 text-left shadow-sm transition-transform hover:-translate-y-0.5">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <FileText size={18} />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
              {status.completedCount}/{status.totalCount}
            </span>
          </div>
          <p className="mt-5 text-lg font-bold text-foreground">Continue setup</p>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {status.remainingCount} task{status.remainingCount === 1 ? '' : 's'} still help improve your account readiness.
          </p>
          <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-700">
            View all tasks
            <ChevronRight size={16} />
          </span>
        </button>

        {previewItems.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => onAction(item.key)}
            className="min-w-[240px] border border-border bg-card p-5 text-left shadow-sm transition-transform hover:-translate-y-0.5">
            <div className="flex items-center justify-between gap-3">
              <ItemStatusIcon item={item} />
              {item.isOptional ? (
                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-700">
                  Recommended
                </span>
              ) : null}
            </div>
            <p className="mt-5 text-base font-bold text-foreground">{item.title}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.description}</p>
            <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-foreground">
              {item.actionLabel}
              <ChevronRight size={16} />
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

export function SetupGuidePanel({ open, title, status, onClose, onAction }: PanelProps) {
  if (!open || !status || status.remainingCount === 0) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/45">
      <button type="button" aria-label="Close setup guide" className="h-full flex-1 cursor-default" onClick={onClose} />
      <div className="flex h-full w-full max-w-md flex-col bg-card shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
        <div className="flex items-start justify-between border-b border-border px-5 py-5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{title}</p>
            <h2 className="mt-2 text-2xl font-bold text-foreground">
              {status.completedCount} of {status.totalCount} completed
            </h2>
          </div>
          <button type="button" title="Close setup guide" aria-label="Close setup guide" onClick={onClose} className="rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="space-y-3">
            {status.items.map((item) => (
              <div key={item.key} className="border border-border bg-background p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2.5">
                      <ItemStatusIcon item={item} />
                      <p className="text-sm font-semibold text-foreground">{item.title}</p>
                      {item.isOptional ? (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-700">
                          Recommended
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.description}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <Button variant={item.isComplete ? 'outline' : 'primary'} onClick={() => onAction(item.key)}>
                    {item.actionLabel}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border px-5 py-4">
          <div>
            <Button className="w-full" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function SetupGuideFloatingButton({ remainingCount, onOpen }: FloatingButtonProps) {
  if (remainingCount === 0) {
    return null
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="fixed bottom-4 right-[150px] z-20 inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-[0_14px_30px_rgba(15,23,42,0.18)] transition-transform hover:-translate-y-0.5 hover:bg-background"
      aria-label="Open setup guide"
      title="Open setup guide"
    >
      <FileText size={18} />
      {remainingCount > 0 ? (
        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
          {remainingCount}
        </span>
      ) : null}
    </button>
  )
}